import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Divider,
  Container,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  GlobalStyles,
  Backdrop,
} from '@mui/material';
import { Microsoft as MicrosoftIcon } from '@mui/icons-material';
import { loginUser, externalLoginUser } from '../componentes/http/actions/authAction';
import Image from '../assets/Alerta Normativa-logo2026-claro.png';

// Google OAuth
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

// Configuración MSAL para Microsoft
import { msalInstance } from '../componentes/security/msal-config';

const LoginForm = () => {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({
    Email: '',
    Password: '',
  });
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [, setToken] = useState('');
  // Estado para controlar qué método de login está activo
  const [activeLoginMethod, setActiveLoginMethod] = useState(null);
  // Estado para bloquear la UI cuando un popup está abierto
  const [isUILocked, setIsUILocked] = useState(false);
  
  // Efecto para limpiar el error después de un tiempo
  useEffect(() => {
    let errorTimeout;
    
    if (loginError) {
      errorTimeout = setTimeout(() => {
        setLoginError('');
      }, 5000);
    }
    
    return () => {
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
    };
  }, [loginError]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setLoginData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Inicio de sesión tradicional
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading || isUILocked) return; 
    
    setLoginError('');
    setIsLoading(true);
    setActiveLoginMethod('standard');

    try {
      const result = await loginUser(loginData, (errorMessage) => {
        setLoginError(errorMessage);
        setIsLoading(false);
        setActiveLoginMethod(null);
      }, (token) => {
        localStorage.setItem('token', token);
        setToken(token); 
      });

      if (result) {
        navigate('/home');
      } else {
        setIsLoading(false);
        setActiveLoginMethod(null);
      }
    } catch (error) {
      console.error('Error durante el inicio de sesión:', error);
      setLoginError(error.message || 'Error de inicio de sesión');
      setIsLoading(false);
      setActiveLoginMethod(null);
    }
  };

  // Inicio de sesión con Google
  const handleGoogleSuccess = async (credentialResponse) => {
    // Desbloquear la UI cuando se completa la autenticación
    setIsUILocked(false);
    
    if (isLoading) return;
    
    setIsLoading(true);
    setLoginError('');
    setActiveLoginMethod('google');

    if (!credentialResponse || !credentialResponse.credential) {
      setLoginError('Error al iniciar sesión con Google. Inténtelo de nuevo.');
      setIsLoading(false);
      setActiveLoginMethod(null);
      return;
    }
    
    try {
      const token = credentialResponse.credential;
      const result = await externalLoginUser('Google', token, setLoginError, setToken);
      
      if (result) {
        navigate('/home');
      }
    } catch (error) {
      console.error('Error al iniciar sesión con Google:', error);
    } finally {
      setIsLoading(false);
      setActiveLoginMethod(null);
    }
  };

  const handleGoogleError = () => {
    // Desbloquear la UI en caso de error
    setIsUILocked(false);
    setLoginError('Error al iniciar sesión con Google. Inténtelo de nuevo.');
    setActiveLoginMethod(null);
    setIsLoading(false);
  };

  // Inicio de sesión con Microsoft
  const handleMicrosoftLogin = async () => {
    if (isLoading || isUILocked) return;
    
    // Bloquear UI mientras se abre el popup
    setIsUILocked(true);
    setIsLoading(true);
    setLoginError('');
    setActiveLoginMethod('microsoft');
    
    try {
      const loginRequest = {
        scopes: ['user.read', 'profile', 'email', 'openid']
      };
      
      await msalInstance.initialize();
      const response = await msalInstance.loginPopup(loginRequest);
      
      if (response && response.idToken) {
        const idToken = response.idToken;
        const result = await externalLoginUser('Microsoft', idToken, setLoginError, setToken);
        
        if (result) {
          navigate('/home');
        }
      } else {
        setLoginError("No se pudo obtener el token de autenticación");
      }
    } catch (error) {
      console.error('Error al iniciar sesión con Microsoft: ' + (error.message || 'Inténtelo de nuevo.'));
    } finally {
      setIsUILocked(false);
      setIsLoading(false);
      setActiveLoginMethod(null);
    }
  };

  // Definición de estilos globales para la animación
  const globalStyles = {
    '@keyframes fadeIn': {
      from: { opacity: 0, transform: 'translateY(-10px)' },
      to: { opacity: 1, transform: 'translateY(0)' }
    }
  };

  return (
    <>
      <GlobalStyles styles={globalStyles} />  
      {/* Backdrop que bloquea la interfaz cuando un popup está abierto */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.2)' 
        }}
        open={isUILocked}
        onClick={() => {
          // Permitir desbloquear la UI haciendo clic en el backdrop
          setIsUILocked(false);
        }}
      />
      
      <Container maxWidth="xs" style={{ minHeight: '100vh' }}>
        <Grid container direction="row" alignItems="center" justifyContent="center" style={{ marginTop: 50 }}>
          <Paper elevation={0} style={{ boxShadow: "rgb(209 209 209 / 25%) 0px 1px 4px, rgb(209 209 209 / 35%) 0px 3px 12px 2px" }}>
            <Grid container style={{ padding: 20 }}>
            <Grid item xs={12} md={12} container justifyContent="center" style={{ marginBottom: 20 }}>
                <img 
                src={Image} 
                alt="Alerta Normativa" 
                style={{ 
                  height: '100px', 
                  display: 'flex',
                  marginBottom: '16px',
                }} 
              />
            </Grid>
            
            {loginError && (
              <Alert 
                severity="error" 
                sx={{ 
                  width: '100%', 
                  mb: 2,
                  animation: 'fadeIn 0.3s ease-in-out',
                  borderRadius: '8px'
                }}
                onClose={() => setLoginError('')}
              >
                {loginError}
              </Alert>
            )}
            <Grid item xs={12} md={12}>
              <Typography 
                variant="h5" 
                align="center"
                sx={{
                  fontWeight: 600,
                  color: '#1e293b',
                  mb: 3
                }}
              >
                Inicio de Sesión
              </Typography>
            </Grid>
            {/* Formulario de inicio de sesión normal */}
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Correo electrónico"
                name="Email"
                autoComplete="email"
                autoFocus
                value={loginData.Email}
                onChange={handleChange}
                disabled={isLoading || isUILocked}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#23a37e',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#23a37e',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#23a37e',
                  }
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="Password"
                label="Contraseña"
                type="password"
                id="password"
                autoComplete="current-password"
                value={loginData.Password}
                onChange={handleChange}
                disabled={isLoading || isUILocked}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#23a37e',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#23a37e',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#23a37e',
                  }
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  backgroundColor: '#23a37e',
                  color: 'white',
                  fontWeight: 600,
                  padding: '12px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: '0 4px 12px rgba(35, 163, 126, 0.3)',
                  '&:hover': {
                    backgroundColor: '#1a7d62',
                    boxShadow: '0 6px 16px rgba(35, 163, 126, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    backgroundColor: '#a0a0a0',
                  },
                  transition: 'all 0.3s ease'
                }}
                disabled={isLoading || isUILocked}
              >
                {activeLoginMethod === 'standard' && isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>
            </Box>
            
            <Divider 
              sx={{ 
                width: '100%', 
                my: 3,
                '&::before, &::after': {
                  borderColor: 'rgba(35, 163, 126, 0.2)',
                }
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  fontWeight: 500
                }}
              >
                O continuar con
              </Typography>
            </Divider>
            
            {/* Botones de inicio de sesión con proveedores externos */}
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Botón de Microsoft */}
              <Button
                fullWidth
                variant="outlined"
                onClick={handleMicrosoftLogin}
                disabled={isLoading || isUILocked}
                sx={{ 
                  borderColor: '#000000ff', 
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  justifyContent: 'center',
                  textTransform: 'none',
                  height: '40px',
                  paddingLeft: '36px',
                  paddingRight: '16px',
                  borderRadius: '6px',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#000000ff',
                    backgroundColor: 'rgba(35, 163, 126, 0.04)',
                  },
                  '&:disabled': {
                    borderColor: '#e0e0e0',
                    color: '#a0a0a0',
                  },
                }}
                startIcon={null}
              >
                <Box 
                  sx={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {activeLoginMethod === 'microsoft' && isLoading ? (
                    <CircularProgress size={20} sx={{ color: '#23a37e' }} />
                  ) : (
                    <MicrosoftIcon sx={{ width: '24px', height: '24px', color: '#000000ff' }} />
                  )}
                </Box>
                
                Iniciar sesión con Microsoft
              </Button>

              {/* Botón de Google */}
              <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  size="large"
                  width={355}
                  logo_alignment="left"
                  theme="filled_black"
                  onClose={() => setIsUILocked(false)}
                />
              </GoogleOAuthProvider>
            </Box>
            
            {/* Indicador de proceso global */}
            {isLoading && !activeLoginMethod && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <CircularProgress size={28} sx={{ color: '#23a37e' }} />
              </Box>
            )}
            
            <Typography 
              variant="body2" 
              color="textSecondary" 
              align="center" 
              sx={{ 
                mt: 3,
                color: '#64748b',
                fontSize: '0.875rem'
              }}
            >
              ¿Problemas para acceder? Contacte a su administrador.
            </Typography>
            </Grid>
          </Paper>
        </Grid>
      </Container>
      </>
  );
};

export default LoginForm;